/* The choices the two home lists - Cars and Hire Driver - are sorted and filtered by,
   each with the dictionary word it is shown in, plus the few rules that decide what a
   choice matches.

   The stored values are the ones the matchers have always compared ("7-seater",
   "3+ Stars", "Under ₹5k"), so a filter kept in a session from before still works;
   only what the customer reads is new. No imports, on purpose: the tests load this
   file as it is. */

export const fill = (text, vars = {}) =>
  Object.entries(vars).reduce((out, [k, v]) => out.split(`{${k}}`).join(String(v)), String(text || ""));

/* One choice's words: a dictionary key (with its numbers filled in), a language named
   in the reader's language, or text that reads the same in every language - a range of
   years or rupees. */
export const optionLabel = (o, t, lang) => (o.key ? fill(t(o.key), o.vars)
  : o.language && lang && lang !== "en" ? languageName(o.language, lang) : o.text || o.value);

/* ── Languages, each in its own script ────────────────────────────────────────
   A Telugu speaker looks for "తెలుగు", not "Telugu". Stored as the English name, as
   the onboarding forms have always saved it. */
export const LANGUAGE_CHOICES = [
  { value: "Telugu", label: "తెలుగు" },
  { value: "Hindi", label: "हिंदी" },
  { value: "English", label: "English" },
  { value: "Tamil", label: "தமிழ்" },
  { value: "Kannada", label: "ಕನ್ನಡ" },
  { value: "Malayalam", label: "മലയാളം" },
  { value: "Urdu", label: "اردو" },
];

const NATIVE = Object.fromEntries(LANGUAGE_CHOICES.map((c) => [c.value.toLowerCase(), c.label]));

/* Each language named in the reader's language. A Telugu reader reads "తెలుగు · హిందీ ·
   ఇంగ్లీష్" - not "हिंदी" in a script they may not read, or "English". In English each
   stays in its own script, as above. */
const LANGUAGE_NAMES = {
  telugu: { te: "తెలుగు", hi: "तेलुगु", ta: "தெலுங்கு", kn: "ತೆಲುಗು" },
  hindi: { te: "హిందీ", hi: "हिंदी", ta: "இந்தி", kn: "ಹಿಂದಿ" },
  english: { te: "ఇంగ్లీష్", hi: "अंग्रेज़ी", ta: "ஆங்கிலம்", kn: "ಇಂಗ್ಲಿಷ್" },
  tamil: { te: "తమిళం", hi: "तमिल", ta: "தமிழ்", kn: "ತಮಿಳು" },
  kannada: { te: "కన్నడ", hi: "कन्नड़", ta: "கன்னடம்", kn: "ಕನ್ನಡ" },
  malayalam: { te: "మలయాళం", hi: "मलयालम", ta: "மலையாளம்", kn: "ಮಲಯಾಳಂ" },
  urdu: { te: "ఉర్దూ", hi: "उर्दू", ta: "உருது", kn: "ಉರ್ದು" },
  marathi: { te: "మరాఠీ", hi: "मराठी", ta: "மராத்தி", kn: "ಮರಾಠಿ" },
  bengali: { te: "బెంగాలీ", hi: "बंगाली", ta: "வங்காளம்", kn: "ಬಂಗಾಳಿ" },
  odia: { te: "ఒడియా", hi: "ओड़िया", ta: "ஒடியா", kn: "ಒಡಿಯಾ" },
  gujarati: { te: "గుజరాతీ", hi: "गुजराती", ta: "குஜராத்தி", kn: "ಗುಜರಾತಿ" },
  punjabi: { te: "పంజాబీ", hi: "पंजाबी", ta: "பஞ்சாபி", kn: "ಪಂಜಾಬಿ" },
};

/* One language's name for the reader: "Hindi" is హిందీ to a Telugu reader, and हिंदी in
   English. A language we have no name for is shown as it was typed. */
export function languageName(value, lang) {
  const raw = String(value || "").trim();
  const key = raw.toLowerCase();
  return LANGUAGE_NAMES[key]?.[lang] || NATIVE[key] || raw;
}

/* "Telugu, English, Hindi" (or an array) → "తెలుగు · English · हिंदी" in English, and
   "తెలుగు · ఇంగ్లీష్ · హిందీ" to a Telugu reader. */
export function nativeLanguageNames(raw, lang) {
  const list = Array.isArray(raw) ? raw : String(raw || "").split(",");
  return list.map((l) => String(l || "").trim()).filter(Boolean)
    .map((l) => languageName(l, lang)).join(" · ");
}

const langOption = (value) => ({ value, text: NATIVE[value.toLowerCase()] || value, language: value });

/* ── Cars ──────────────────────────────────────────────────────────────────── */

/* `needs`: a sort that answers nothing yet is not offered yet - a price needs the
   route, and "nearest" needs the pickup. */
export const CAR_SORTS = [
  { value: "RECOMMENDED", key: "hm_sortRec" },
  { value: "PRICE_LOW", key: "hm_sortPriceLow", needs: "route" },
  { value: "PRICE_HIGH", key: "hm_sortPriceHigh", needs: "route" },
  { value: "RATING", key: "hm_sortRating" },
  { value: "ETA", key: "hm_sortEta", needs: "pickup" },
  { value: "NEWEST_CAR", key: "hm_sortNewest" },
];

export const CAR_TYPES = [
  { value: "Hatchback", key: "hm_ctHatch" },
  { value: "Sedan", key: "hm_ctSedan" },
  { value: "Mini SUV", key: "hm_ctMiniSuv" },
  { value: "SUV", key: "hm_ctSuv" },
  { value: "MPV", key: "hm_ctMpv" },
  { value: "Tempo Traveller", key: "hm_ctTempo" },
  { value: "Bus", key: "hm_ctBus" },
];

/* Which price categories each car type covers - by the category's key, not by the name
   on screen. Matched by name, "Tempo Traveller" never equalled "Tempo Traveller (12
   Seater)" and found nothing, and "Mini Bus" left out every bus of twenty seats and
   up. "MPV Luxury" and "Mini Bus" are the old names, still arriving from sessions
   saved before this, and mean what they meant. */
const SEVEN_SEATER = /^SUV_7_(ECO|STD|COMFORT|LUX)$/;
const TYPE_FAMILIES = {
  hatchback: (k) => k.startsWith("HATCHBACK"),
  sedan: (k) => k.startsWith("SEDAN"),
  "mini suv": (k) => k.startsWith("MINI_SUV"),
  suv: (k) => k === "SUV_5" || k === "SUV_7_PREMIUM",
  mpv: (k) => SEVEN_SEATER.test(k),
  "mpv luxury": (k) => SEVEN_SEATER.test(k),
  "tempo traveller": (k) => k.startsWith("TEMPO_TRAVELLER"),
  bus: (k) => k.startsWith("MINI_BUS") || k.startsWith("BUS_"),
  "mini bus": (k) => k.startsWith("MINI_BUS") || k.startsWith("BUS_"),
};

/* category is what detectCarCategory returns: { key, label }. A category an admin
   created is none of the families, and is matched by its name as before. */
export function carTypeMatches(option, category) {
  const name = String(option || "").trim().toLowerCase();
  const family = TYPE_FAMILIES[name];
  if (family) return family(String(category?.key || ""));
  return !!name && String(category?.label || "").trim().toLowerCase() === name;
}

/* A price category in the reader's words: in English its own name as before ("Sedan
   Standard"); in the other languages the car type it is one of ("సెడాన్", "7 సీటర్
   (ఎంపీవీ)"). `label` is the English name, from the category table. */
export function categoryWords(key, label, t, lang) {
  const english = label || String(key || "").replace(/_/g, " ");
  if (!lang || lang === "en") return english;
  const type = CAR_TYPES.find((o) => TYPE_FAMILIES[o.value.toLowerCase()]?.(String(key || "")));
  return type ? t(type.key) : english;
}

// "7-seater" is the shape matchesSeats reads.
export const seatsValue = (n) => `${n}-seater`;
export const seatsIn = (value) => Number(String(value || "").replace(/[^0-9]/g, "")) || null;

export const FUELS = [
  { value: "Petrol", key: "hm_fuelPetrol" },
  { value: "Diesel", key: "hm_fuelDiesel" },
  { value: "CNG", key: "hm_fuelCng" },
  { value: "EV", key: "hm_fuelEv" },
];
const FUEL_WORDS = { petrol: "hm_fuelPetrol", diesel: "hm_fuelDiesel", cng: "hm_fuelCng", electric: "hm_fuelEv", ev: "hm_fuelEv" };

/* The fuel as the owner wrote it ("Diesel", "Petrol + CNG"), each known word in the
   reader's language; anything else as written. */
export function fuelLabel(raw, t) {
  return String(raw || "").split(/\s*[+/&,]\s*/).map((w) => w.trim()).filter(Boolean)
    .map((w) => (FUEL_WORDS[w.toLowerCase()] ? t(FUEL_WORDS[w.toLowerCase()]) : w)).join(" + ");
}

export const YEARS = [
  { value: "2024+", key: "hm_yr2024" },
  { value: "2020-23", text: "2020 – 2023" },
  { value: "2016-19", text: "2016 – 2019" },
  { value: "≤2015", key: "hm_yr2015" },
];

export const BUDGETS = [
  { value: "Under ₹5k", key: "hm_budUnder5" },
  { value: "₹5k-₹10k", text: "₹5,000 – 10,000" },
  { value: "₹10k-₹15k", text: "₹10,000 – 15,000" },
  { value: "Above ₹15k", key: "hm_budAbove15" },
];

/* The Drivers list's sort and fare bands (Oct 2026, mock Fix_C changes 1 and 8). A
   driver's fare is for their time, so the bands are smaller than a car's. */
export const DRIVER_SORTS = [
  { value: "RECOMMENDED", key: "hm_sortRec" },
  { value: "PRICE_LOW", key: "hm_sortFareLow", needs: "route" },
  { value: "EXPERIENCE", key: "hm_sortExp" },
  { value: "RATING", key: "hm_sortRating" },
  { value: "ONLINE", key: "hm_sortOnline" },
  { value: "NEAREST", key: "hm_sortEta", needs: "pickup" },
];
export const DRIVER_BUDGETS = [
  { value: "Under ₹1k", key: "hm_dBudUnder1", max: 1000 },
  { value: "₹1k-₹2k", text: "₹1,000 – 2,000", min: 1000, max: 2000 },
  { value: "₹2k-₹3k", text: "₹2,000 – 3,000", min: 2000, max: 3000 },
  { value: "Above ₹3k", key: "hm_dBudAbove3", min: 3000 },
];
/** Whether a fare sits in any of the chosen bands ("Under ₹1k,₹2k-₹3k"). */
export function inDriverBudget(value, amount) {
  const bands = splitValues(value).map((v) => DRIVER_BUDGETS.find((b) => b.value === v)).filter(Boolean);
  if (!bands.length) return true;
  if (!(Number(amount) > 0)) return false;
  return bands.some((b) => (b.min == null || amount >= b.min) && (b.max == null || amount < b.max));
}

// A range typed in before this page lost its min/max boxes: "CUSTOM:3000-8000".
export function customBudgetText(value) {
  if (!String(value || "").startsWith("CUSTOM:")) return null;
  const [min, max] = String(value).slice(7).split("-");
  const rs = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
  return max ? `${rs(min)} – ${rs(max)}` : `${rs(min)}+`;
}

export const RATINGS = [
  { value: "3+ Stars", key: "hm_starsPlus", vars: { n: 3 } },
  { value: "4+ Stars", key: "hm_starsPlus", vars: { n: 4 } },
];

export const EXPERIENCE = [
  { value: "3+ Years", key: "hm_expPlus", vars: { n: 3 } },
  { value: "5+ Years", key: "hm_expPlus", vars: { n: 5 } },
  { value: "10+ Years", key: "hm_expPlus", vars: { n: 10 } },
];

export const CAR_LANGS = ["Telugu", "Hindi", "English", "Tamil", "Kannada"].map(langOption);

export const TAGS = [
  { value: "On Time", key: "hm_tOnTime" },
  { value: "Well Behaved", key: "hm_tPolite" },
  { value: "Comfortable", key: "hm_tComfort" },
  { value: "Cleanliness", key: "hm_tClean" },
  { value: "Safe Driver", key: "hm_tSafe" },
];

export const AGES = ["19-30", "31-45", "46-60", "60+"].map((r) => ({ value: r, key: "hm_ageRange", vars: { range: r } }));

/* ── Hire drivers ──────────────────────────────────────────────────────────── */

export const DRIVER_LANGS = ["Telugu", "Hindi", "English", "Tamil", "Kannada", "Urdu"].map(langOption);

export const DRIVER_TRIPS = [
  { value: "Local", key: "hm_ttLocal" },
  { value: "Outstation", key: "hm_ttOut" },
  { value: "Dropping", key: "hm_ttDrop" },
];

/* The trips a driver takes are saved as "Local", "Outstation" and "Droppings"; older
   profiles say "Full Day", "Half Day" or "Local Trips". The old filter looked only for
   the old words, so "Local" found no one registered since. */
const TRIP_WORDS = { Local: /local|full day|half day/i, Outstation: /outstation/i, Dropping: /drop/i };
export function driverTripMatches(choices, availability) {
  const have = Array.isArray(availability) ? availability : [];
  return choices.some((c) => TRIP_WORDS[c] && have.some((a) => TRIP_WORDS[c].test(String(a || ""))));
}

/* The trips a driver takes, in the filter's words - the old saved words with the new
   ("Full Day" is Local). Anything else is shown as it was saved. */
export function driverTripWords(availability, t) {
  const out = [];
  (Array.isArray(availability) ? availability : []).forEach((a) => {
    const s = String(a || "").trim();
    const opt = DRIVER_TRIPS.find((o) => TRIP_WORDS[o.value].test(s));
    const word = opt ? t(opt.key) : s;
    if (word && !out.includes(word)) out.push(word);
  });
  return out;
}

/* The lower end of a driving-experience band: "10-15 years" → 10, "15+ years" → 15,
   "0-1 year" → 0. A plain number of years is itself. Unknown → null.
   The old filter compared the band's text with "3-5 Years" and "10+ Years", which the
   stored bands never contain, so it found no one. */
export function expFloor(raw) {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  const m = String(raw || "").match(/\d+/);
  return m ? Number(m[0]) : null;
}

/* Not "Busy", "Unavailable" or "Offline". The old test looked for "available" in the
   status - which "unavailable" contains - so every driver passed it. */
export const isAvailableNow = (status) => !["busy", "unavailable", "offline"].includes(String(status || "").trim().toLowerCase());

/* ── The one badge on a car's photo ────────────────────────────────────────────
   The list card and the car's own page wear the same one, so a car never says
   "Nearby" on one and "Family trip" on the other. At most one, in this order:
   top rated, nearby, luxury, family, quick.
   "Nearby" is how far THIS CAR is from the pickup (distanceFromPickupKm, from the same
   measurement the radius listing uses), never the trip's own length - that badged
   every car on a short trip and none on a long one. No measurement, no badge. */
export const NEARBY_RADIUS_KM = 5;
export function featureBadgeKey(car, { avgRating = null, categoryKey = "", seats = 0 } = {}) {
  const raw = car?.distanceFromPickupKm;
  const km = raw === null || raw === undefined || raw === "" ? NaN : Number(raw);
  if (car?.topRated || Number(avgRating) >= 4.8) return "hm_bTop";
  if (Number.isFinite(km) && km <= NEARBY_RADIUS_KM) return "hm_bNearby";
  if (car?.luxury || categoryKey === "LUXURY") return "hm_bLuxury";
  if (car?.familyTrip || Number(seats) >= 6) return "hm_bFamily";
  if (car?.fastBooking) return "hm_bFast";
  return null;
}

/* ── Both ──────────────────────────────────────────────────────────────────── */

// A choice's value, from a comma list as the filters store them.
export const splitValues = (v) => String(v || "").split(",").map((x) => x.trim()).filter(Boolean);
